import { TemplateType } from '@components/Common/CommonEnums';
import {
  DeckColumnDeleteItemsInput,
  DeckColumnItemsInput,
  DenominatorLabelValues,
  KPIDrivers,
} from '@services/api/deckManagement/graphql/schema/API';
import { ReactNode } from 'react';
import { LockStatus } from 'types/DeckContext.types';
import { PageGranularity } from 'types/Page.types';
import { EnhancedTOCEntry } from 'types/TOCContext.types';

/**
 * Expected format for CREATE, UPDATE, and DELETE items to be sent when defining or updating Page template information.
 */
export interface TemplateCRUDItems {
  create: DeckColumnItemsInput[];
  update: DeckColumnItemsInput[];
  delete: DeckColumnDeleteItemsInput[];
}

/** Per-section template configuration for mapping, block headers, and block count */
export interface SectionTemplateConfig {
  mapping: string;
  blockHeaders: Record<string, string>;
  numberOfBlockHeaders: number;
}

/** Snapshot of table configuration selections for change detection */
export interface TableConfigState {
  templateType: TemplateType;
  selectedTemplate: string;
  grainTemplates: Record<string, string>;
  grainMappings: Record<string, string>;
  grainBlockHeaders: Record<string, Record<string, string>>;
  sectionTemplateConfigs: Record<string, SectionTemplateConfig>;
}

export enum PageSetupSteps {
  PAGE_DETAILS = 'Page Details',
  TABLE_CONFIGURATION = 'Table Configuration',
  PAGE_DRIVER = 'Page Driver',
}

export interface PageSetupContextType {
  // Stepper state
  pageSetupSteps: PageSetupSteps[];
  currentStep: PageSetupSteps | null;
  nextStep: PageSetupSteps | null;
  setCurrentStep: (currentStep: PageSetupSteps) => void;

  // Route-derived state
  currDeckId: string | null;
  currPageName: string | null;

  // Created or existing TOC entry ID
  tocId: string | null;
  setTocId: (tocId: string | null) => void;
  // Resolved TOC entry from the current modifiedTocEntries (used to obtain latest internalId after initial creation)
  tocEntry: EnhancedTOCEntry | undefined;
  // Indicates whether a TOCEntry and its underlying grain Pages have been created by the backend
  isPageCreated: boolean;
  setIsPageCreated: (value: boolean) => void;

  // Page Details Step state
  pageName: string;
  setPageName: (newPageName: string) => void;
  tocName: string;
  setTocName: (newTocName: string) => void;
  pageHeaderName: string;
  setPageHeaderName: (newPageHeaderName: string) => void;
  reportingPeriods: PageGranularity[];
  setReportingPeriods: (newReportingPeriods: PageGranularity[]) => void;
  /** Tracks denominator_label field of Page Entity and associated Page Setup Wizard Step **/
  denominatorLabel: DenominatorLabelValues | null;
  setDenominatorLabel: (newDenominatorLabel: DenominatorLabelValues | null) => void;

  // Table Configuration Step state
  /** Single template for all grains, or Granular per-grain templates */
  templateType: TemplateType;
  setTemplateType: (type: TemplateType) => void;
  /** Number of block headers to assign to Page (Max determined by MAX_BLOCK_HEADERS; 1 by default) */
  numberOfBlockHeaders: number;
  setNumberOfBlockHeaders: (count: number) => void;
  /** Selected column template (Single mode) */
  selectedTemplate: string;
  setSelectedTemplate: (template: string) => void;
  /** Selected column mapping group (Single mode) */
  selectedMapping: string;
  setSelectedMapping: (mapping: string) => void;
  /** Block header label assignments (Single mode) */
  blockHeaders: Record<string, string>;
  setBlockHeaders: (
    headers: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>),
  ) => void;
  /** Per-Grain template selections (Granular mode) */
  grainTemplates: Record<string, string>;
  setGrainTemplates: (
    templates: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>),
  ) => void;
  /** Per-grain mapping selections (Granular mode) */
  grainMappings: Record<string, string>;
  setGrainMappings: (
    mappings: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>),
  ) => void;
  /** Per-grain block header assignments (Granular mode) */
  grainBlockHeaders: Record<string, Record<string, string>>;
  setGrainBlockHeaders: (
    headers:
      | Record<string, Record<string, string>>
      | ((prev: Record<string, Record<string, string>>) => Record<string, Record<string, string>>),
  ) => void;

  // Multi-section edit mode
  /** Detected page sections from deck columns (e.g. ['1', '2', '3', '4']) */
  pageSections: string[];
  /** Currently active section tab in Table Configuration */
  activePageSection: string;
  setActivePageSection: (section: string) => void;
  /** Per-section mapping and block header configurations */
  sectionTemplateConfigs: Record<string, SectionTemplateConfig>;

  /** Computed CRUD changeset for column template operations, ready for final save */
  templateCRUDItems: TemplateCRUDItems;

  /** Whether the table configuration has changed from its initial persisted state */
  hasTableConfigChanged: boolean;

  /** Updates the baseline snapshot after a successful save */
  snapshotTableConfig: () => void;

  /** Restores table configuration to the initial persisted state */
  resetTableConfig: () => void;

  /** Whether we are dealing with an Update template request (set true after first template save) */
  isUpdateTemplate: boolean;
  setIsUpdateTemplate: (value: boolean) => void;

  /** Dynamically determined available grains from pagesByName */
  availableGrains: string[];

  // Page Drivers Step state
  /** Current page driver dimension selections */
  pageDrivers: KPIDrivers;
  /** Setter for page driver state */
  setPageDrivers: React.Dispatch<React.SetStateAction<KPIDrivers>>;
  /** Persists page driver changes via SaveDeck mutation */
  savePageDrivers: (successCallback?: () => void, failureCallback?: (error: Error) => void) => void;
  /** Whether a save operation is currently in progress */
  pageDriversSaving: boolean;
  /** Whether decklines are still loading for the current page */
  isDecklinesLoading: boolean;
  /** Error message from Page-related requests (if any) */
  pageError: string | null;
  /**
   * Lock state for the page being set up (all grains share one lock). Editing the wizard
   * requires holding THIS page's lock; independent of the deck lock.
   */
  pageLockStatus: LockStatus;
}

export interface PageSetupProviderProps {
  children?: ReactNode;
}
